import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from "@angular/core";
import {Router} from "@angular/router";
import {Api} from "../../../../../../common/services/api.service";
import {Base} from "../../../../../../common/interfaces/base.interfaces";
import {SpinnerService} from "../../../../../../common/services/spinner.service";
import {Table} from "../../../../../../common/interfaces/table.interface";
import {ConfirmComponent} from "../../../../../../common/components/confirm/confirm.component";
import {ToastService} from "../../../../../../common/services/toast.service";
import {PartnerService} from "../../../../../../common/services/partner.service";
import {WarehouseService} from "../../../../../../common/services/warehouse.service";
import {Api3Service} from "../../../../../../common/services/api3.service";
import {Table2Component} from "../../../../../../common/components/table2";
import {UserService} from "../../../../../../common/services/user.service";
import {FormControl} from "@angular/forms";
import {takeUntil} from "rxjs";
import {StorageService} from "../../../../../../common/services/storage.service";


@Component({
    selector: "section-custom-labels-list",
    template: `
        <h1 class="container-heading">Custom labels</h1>
        <mat-card>
            <mat-card-content>
                <common-table2 *ngIf="tableSettings" [settings]="tableSettings">
                    <div row1>
                        <button color="accent" mat-raised-button (click)="add()">
                            Add
                        </button>

                        <mat-checkbox
                            *userAllowed="['templates_custom_labels']"
                            [formControl]="show_templates">Show templates
                        </mat-checkbox>
                    </div>
                </common-table2>
            </mat-card-content>
        </mat-card>
        <common-confirm></common-confirm>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class CustomLabelsListComponent implements OnInit, Base.IComponent, OnDestroy {

    protected destroy$: EventEmitter<boolean> = new EventEmitter(false);

    public readonly state: Base.IState;

    public tableSettings: Table.ISettings;

    @ViewChild(ConfirmComponent, {static: true})
    public confirm: ConfirmComponent;

    @ViewChild(Table2Component, {static: false})
    public tableRef: Table2Component;

    public show_templates: any = new FormControl(false);

    public constructor(
        private router: Router,
        private api3Service: Api3Service,
        private spinnerService: SpinnerService,
        private toastService: ToastService,
        private userService: UserService,
        private storageService: StorageService,
    ) {
    }

    private getTableUrl(): Table.ITableApi {
        return {
            url: [this.state.section, "custom-labels"],
            query: {
                relations: [
                    "Size"
                ],
                show_templates: this.show_templates.value,
            },
            version: 3
        };
    }

    private prepareTable(): void {
        this.tableSettings = {
            table_id: "sdf9ijkmndrt7is",
            api: this.getTableUrl(),
            actions: [
                {
                    name: "view",
                    title: "View",
                    click: (row: any): void => {
                        this.router.navigate([
                            this.state.section,
                            this.state.component,
                            "edit",
                            "id",
                            row.id
                        ]);
                    }
                },
                {
                    name: "copy",
                    title: "Create copy",
                    click: (row: any): void => {
                        this.add(row.id);
                    },
                    icon: "content_copy",
                    disabled: !this.userService.validatePermissions("edit_custom_labels"),
                },
                {
                    name: "delete",
                    title: "Delete",
                    click: async (row: any): Promise<any> => {
                        if (!await this.confirm
                            .confirm("Are you sure want to delete this label? This action cannot be undone!")) {
                            return;
                        }
                        this.spinnerService.show();
                        const {message, code}: Api.IResponse = await this.api3Service.delete(
                            `${this.state.section}/custom-labels/${row.id}`,
                        );
                        this.spinnerService.hide();
                        if (code === 200) {
                            this.toastService.show(message, "success");
                            this.tableRef.reload();
                        }
                    },
                    disabledFn: (row: any): boolean => {
                        return !(row.partner_id === PartnerService.partner?.id
                            || row.warehouse_id === WarehouseService.warehouse?.id);
                    }
                }
            ],
            columns: [
                {
                    data: "name",
                    title: "Name",
                },
                {
                    data: "type",
                    title: "Type",
                },
                {
                    data: "size.width",
                    name: "Size.width",
                    title: "Width, mm",
                    searchable: false
                },
                {
                    data: "size.height",
                    name: "Size.height",
                    title: "Height, mm",
                    searchable: false,
                    render: (row: any): string => {
                        return row.size.height == 0 ? "auto" : row.size.height;
                    }
                },
                {
                    data: "created_at",
                    title: "Created at",
                    searchable: false
                }
            ]
        };
    }

    public add(clone_id: number = null): void {
        const url: string[] = [
            this.state.section,
            this.state.component,
            "add"
        ];

        if (clone_id) {
            url.push("id");
            url.push("" + clone_id);
        }

        this.router.navigate(url);
    }

    public ngOnInit(): void {
        if (this.userService.validatePermissions("templates_custom_labels")
            && this.storageService.get(this.state.section + "-show-templates", false)) {
            this.show_templates.setValue(true);
        }

        this.prepareTable();

        this.show_templates.valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe((value: boolean) => {
                this.tableRef.reload(this.getTableUrl());
                this.storageService.set(this.state.section + "-show-templates", value);
            });
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    public ngConfig(): Base.IConfig {
        return {
            name: "custom-labels",
            actions: {
                "browse": ["browse_custom_labels"]
            }
        };
    }
}
